import { visit } from '@hotwired/turbo'

const POLL_INTERVAL = 20000
const LAST_ID_KEY = 'notifications:lastId'
const TITLE_COUNT = /^\(\d+\+?\) /

// The navbar bell (see lib/notifications.rb). While the tab is visible it polls for the unread count and new
// notifications, shows each new one as a clickable toast, and keeps the count in the tab title.
export default class NotificationBell extends HTMLElement {
  connectedCallback () {
    this.lastId = Math.max(Number(this.dataset.lastId) || 0, this.storedLastId())
    this.storeLastId()
    this.renderCount(Number(this.dataset.unreadCount) || 0)

    this.onVisibilityChange = () => { if (!document.hidden) this.poll() }
    this.onOpen = () => this.openMenu()
    // "Mark all as read" re-renders the menu, which drops focus and would close the dropdown; keep it open.
    this.onMenuSubmit = () => { this.keepMenuOpen = true }
    this.onFrameLoad = () => {
      if (this.keepMenuOpen) {
        this.refocusing = true
        this.querySelector('[data-notification-bell-button]')?.focus({ preventScroll: true })
        this.refocusing = false
      }
      this.keepMenuOpen = false
      this.poll()
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.menu?.addEventListener('turbo:submit-start', this.onMenuSubmit)
    this.menu?.addEventListener('turbo:frame-load', this.onFrameLoad)
    this.querySelector('[data-notification-bell-button]')?.addEventListener('focus', this.onOpen)

    this.interval = window.setInterval(() => this.poll(), POLL_INTERVAL)
  }

  disconnectedCallback () {
    window.clearInterval(this.interval)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.menu?.removeEventListener('turbo:submit-start', this.onMenuSubmit)
    this.menu?.removeEventListener('turbo:frame-load', this.onFrameLoad)
    this.querySelector('[data-notification-bell-button]')?.removeEventListener('focus', this.onOpen)
  }

  get menu () {
    return this.querySelector('[data-notification-menu]')
  }

  get toastContainer () {
    return this.querySelector('[data-notification-toasts]')
  }

  // The menu loads the first time it opens (not with the page, so page loads cost nothing extra); after that it
  // reloads on each open, and when new notifications arrive, so it's never stale.
  openMenu () {
    if (this.refocusing) return

    if (this.menu && !this.menu.src) {
      this.menu.src = this.menu.dataset.src
    } else {
      this.refreshMenu()
    }
  }

  // Back to the newest page if the user paged to older ones.
  refreshMenu () {
    if (!this.menu?.src || !this.menu.complete) return

    const newest = new URL(this.menu.dataset.src, window.location.origin).href

    if (this.menu.src === newest) {
      this.menu.reload()
    } else {
      this.menu.src = newest
    }
  }

  async poll () {
    if (document.hidden || this.polling || this.stopped) return

    this.polling = true

    try {
      const url = new URL(this.dataset.pollUrl, window.location.origin)
      url.searchParams.set('after', this.lastId)

      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })

      // Signed out or the feature was switched off: stop until the next page load.
      if (!response.ok || response.redirected || !response.headers.get('content-type')?.includes('json')) {
        this.stopped = true
        return
      }

      const data = await response.json()

      if (data.new_count > data.toasts.length) {
        this.showSummaryToast(data.new_count)
      } else {
        data.toasts.forEach((toast) => this.showToast(toast))
      }
      if (data.last_id > this.lastId) this.refreshMenu()
      this.lastId = Math.max(this.lastId, data.last_id)
      this.storeLastId()
      this.renderCount(data.unread_count)
    } catch (_e) {
      // Offline or a deploy in progress; try again on the next tick.
    } finally {
      this.polling = false
    }
  }

  renderCount (count) {
    const badge = this.querySelector('[data-notification-badge]')
    const button = this.querySelector('[data-notification-bell-button]')

    if (badge) {
      badge.textContent = count > 99 ? '99+' : String(count)
      badge.classList.toggle('hidden', count === 0)
    }

    if (button) {
      button.setAttribute('aria-label', count > 0 ? this.dataset.unreadLabel.replace('__COUNT__', count) : this.dataset.label)
    }

    const title = document.title.replace(TITLE_COUNT, '')
    document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${title}` : title
  }

  showToast (toast) {
    const template = this.querySelector(`template[data-notification-toast-template="${toast.kind}"]`)
    if (!template || !this.toastContainer) return

    const fragment = template.content.cloneNode(true)
    const link = fragment.querySelector('[data-notification-toast-link]')

    fragment.querySelector('[data-notification-toast-title]').textContent = toast.title
    fragment.querySelector('[data-notification-toast-document]').textContent = toast.document
    // A real link to the document, so Cmd/Ctrl-click and middle-click open it in a new tab too; either way the
    // notification is marked read.
    link.href = toast.url
    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        this.markRead(toast.open_url)
      } else {
        event.preventDefault()
        this.open(toast)
      }
    })
    link.addEventListener('auxclick', (event) => { if (event.button === 1) this.markRead(toast.open_url) })

    this.toastContainer.prepend(fragment) // newest on top
  }

  // Too many at once to show one by one (e.g. a bulk send): one toast that opens the bell.
  showSummaryToast (count) {
    const template = this.querySelector('template[data-notification-toast-template="summary"]')
    if (!template || !this.toastContainer) return

    const fragment = template.content.cloneNode(true)
    const link = fragment.querySelector('[data-notification-toast-link]')

    fragment.querySelector('[data-notification-toast-title]').textContent = this.dataset.summaryLabel.replace('__COUNT__', count)
    fragment.querySelector('[data-notification-toast-document]').textContent = this.dataset.summaryHint
    link.removeAttribute('href')
    link.setAttribute('role', 'button')
    link.setAttribute('tabindex', '0')
    link.addEventListener('click', (event) => {
      event.preventDefault()
      link.closest('flash-message')?.dismiss()
      this.querySelector('[data-notification-bell-button]')?.focus()
    })

    this.toastContainer.prepend(fragment)
  }

  async open (toast) {
    await this.markRead(toast.open_url)

    visit(toast.url)
  }

  markRead (openUrl) {
    return fetch(openUrl, {
      method: 'POST',
      keepalive: true,
      headers: { Accept: 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      credentials: 'same-origin'
    }).catch(() => {})
  }

  storedLastId () {
    try {
      return Number(window.sessionStorage.getItem(LAST_ID_KEY)) || 0
    } catch (_e) {
      return 0
    }
  }

  storeLastId () {
    try {
      window.sessionStorage.setItem(LAST_ID_KEY, String(this.lastId))
    } catch (_e) {}
  }
}
